import React, { createContext, useContext, useEffect, useState } from "react";

// ThemeProvider sem deps externas. Gerencia 3 estados: "light", "dark", "system".
// "system" segue prefers-color-scheme do SO em tempo real.
//
// Status no startkit: configurado mas NÃO engatilhado por padrão.
// Para ativar, envolva o App no entrypoint:
//
//   import { ThemeProvider } from "@/components/ui/theme-provider";
//   <ThemeProvider defaultTheme="system" storageKey="rdmapps-theme">
//     <App ... />
//   </ThemeProvider>
//
// E coloque o <ThemeToggle /> onde quiser permitir alternar.

const ThemeProviderContext = createContext({
  theme: "system",
  setTheme: () => null,
});

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
  // Sem UI de troca de tema, valores antigos gravados no localStorage
  // prenderiam o usuário num tema inalcançável — ignoreStored força o default.
  ignoreStored = false,
  ...props
}) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined" || ignoreStored) return defaultTheme;
    return localStorage.getItem(storageKey) || defaultTheme;
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  // Atualiza dinamicamente quando o sistema muda (apenas em modo "system")
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      const root = window.document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const value = {
    theme,
    setTheme: (newTheme) => {
      localStorage.setItem(storageKey, newTheme);
      setThemeState(newTheme);
    },
  };

  return (
    <ThemeProviderContext.Provider value={value} {...props}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
